/** Капитал: снимок, кривая и кошельки.
 *
 * Не баланс счетов — датированный срез «что где лежит», который человек вносит
 * руками (§16.16, D-11). Вся история лежит в базе с первого дня, а чат показывал
 * только последний снимок: кривая — то, ради чего снимки и собирались.
 */
import { useEffect, useState } from "react";
import {
  ApiError, api,
  type Capital as CapitalData, type CapitalPoint, type Failure, type Pot,
} from "../lib/api.ts";
import { daysBetween, sameCyclePoint } from "../lib/due.ts";
import { formatMoney } from "../lib/money.ts";
import { plural } from "../lib/plural.ts";
import { rowButton } from "../lib/rowStyle.ts";
import { tap } from "../lib/telegram.ts";
import { useOnce } from "../lib/once.ts";
import { Sparkline } from "../components/Sparkline.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { Fallback } from "./Fallback.tsx";
import { card, hint } from "../components/ui.tsx";

export function Capital({ onOpen }: {
  onOpen: (page: "goals" | "funds" | "savings") => void;
}) {
  const [data, setData] = useState<CapitalData | null>(null);
  const [points, setPoints] = useState<CapitalPoint[]>([]);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Pot | null>(null);

  const load = () => {
    setLoading(true);
    setFailure(null);
    Promise.all([api.capital(), api.capitalHistory()])
      .then(([capital, history]) => { setData(capital); setPoints(history); })
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  return (
    <div style={{ padding: "var(--screen)" }}>
      <h1 style={{ fontSize: "var(--f-t2)", fontWeight: 700, letterSpacing: "-0.3px",
                   margin: "0 0 var(--s-4)" }}>
        Капитал
      </h1>

      {loading ? <Skeleton /> : data === null ? <Empty onAdd={() => setAdding(true)} /> : (
        <>
          <section style={card}>
            <div className="num" style={{ fontSize: "var(--f-t1)", fontWeight: 700,
                                          letterSpacing: "-0.8px" }}>
              {formatMoney(data.total, data.base_currency)}
            </div>
            <Trend data={data} />
            <Sparkline points={points} />
            {points.length === 1 && (
              <p style={hint}>Первый снимок — сравнивать пока не с чем. Кривая появится со вторым.</p>
            )}
          </section>

          <h2 style={{ fontSize: "var(--f-t3)", fontWeight: 600, margin: "var(--s-6) 0 var(--s-3)" }}>
            По кошелькам
          </h2>
          <div style={{ ...card, padding: "2px var(--s-5)" }}>
            {data.pots.map((pot, i) => (
              <PotRow key={pot.name} pot={pot} base={data.base_currency}
                      last={i === data.pots.length - 1} onOpen={() => setEditing(pot)} />
            ))}
          </div>

          <button type="button" onClick={() => { tap(); setAdding(true); }}
                  style={{ ...primary, marginTop: "var(--s-4)" }}>
            Обновить кошельки
          </button>

          {/* Цели и накопления живут здесь, а не отдельными табами: это всё про
              то, что накоплено, а не про то, что потрачено. */}
          <div style={{ ...card, padding: "2px var(--s-5)", marginTop: "var(--s-6)" }}>
            <Link label="Цели" hint="сколько нужно в месяц, чтобы успеть"
                  onOpen={() => onOpen("goals")} />
            <Link label="Копилки" hint="деньги, отложенные под конкретную трату"
                  onOpen={() => onOpen("funds")} />
            <Link label="Накопления" hint="норма сбережений, буфер, распределение"
                  onOpen={() => onOpen("savings")} last />
          </div>
        </>
      )}

      {adding && <SnapshotSheet previous={data?.pots ?? []} onClose={() => setAdding(false)}
                                onDone={() => { setAdding(false); load(); }} />}
      {editing && <PotSheet pot={editing} onClose={() => setEditing(null)}
                            onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Link({ label, hint: sub, onOpen, last }: {
  label: string; hint: string; onOpen: () => void; last?: boolean;
}) {
  return (
    <button type="button" onClick={() => { tap(); onOpen(); }}
      style={{ display: "flex", alignItems: "center", gap: "var(--s-3)", width: "100%",
               padding: "13px 0", minHeight: "var(--tap)", background: "none", border: "none", font: "inherit",
               color: "inherit", cursor: "pointer", textAlign: "left",
               borderBottom: last ? "none" : "1px solid var(--separator)" }}>
      <span style={{ flexGrow: 1 }}>
        <span style={{ display: "block", fontSize: "var(--f-body)",
                       fontWeight: 500 }}>{label}</span>
        <span style={{ display: "block", fontSize: "var(--f-caption)",
                       color: "var(--text-muted)", marginTop: 2 }}>{sub}</span>
      </span>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--text-disabled)"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 6l6 6-6 6" />
      </svg>
    </button>
  );
}

function Trend({ data }: { data: CapitalData }) {
  if (data.delta === null) {
    return <p style={hint}>Первый снимок — сравнивать не с чем.</p>;
  }
  // Дельту считает домен и присылает строкой: вычитание денег через Number()
  // даёт 412.4500000000003 — ровно та потеря копеек, ради которой MA-INV-6.
  const grew = !data.delta.startsWith("-");
  // Насколько снимки отстоят друг от друга. Прирост за три дня выглядит как
  // месячный, хотя это просто упавшая зарплата, — поэтому интервал, не похожий
  // на месяц, называем вслух, а не оставляем вычислять по датам.
  const span = data.previous_on === null ? null : daysBetween(data.previous_on, data.taken_on);
  const comparable = data.previous_on !== null && sameCyclePoint(data.previous_on, data.taken_on);
  return (
    <div style={{ fontSize: "var(--f-caption)", marginTop: 4 }}>
      <span className="num" style={{ color: grew ? "var(--income)" : "var(--text)", fontWeight: 600 }}>
        {grew ? "+" : ""}{formatMoney(data.delta, data.base_currency)}
      </span>
      <span style={{ color: "var(--text-muted)" }}>
        {comparable || span === null
          ? ` с ${formatDay(data.previous_on)}`
          : ` за ${plural(span, ["день", "дня", "дней"])} с ${formatDay(data.previous_on)}`}
      </span>
      {/* Сравниваем со строкой: сумма не должна проходить через Number даже
          ради условия видимости — правило одно на все деньги. */}
      {data.fx_effect.replace(/[0.]/g, "") !== "" && (
        <span style={{ color: "var(--text-muted)" }}>
          {" "}· из них {formatMoney(data.fx_effect, data.base_currency)} дал курс
        </span>
      )}
      {!comparable && span !== null && (
        <p style={{ ...hint, marginTop: "var(--s-2)", marginBottom: 0 }}>
          Прошлый снимок на другое число месяца, поэтому с соседними месяцами
          этот прирост не сравнить.
        </p>
      )}
    </div>
  );
}

function PotRow({ pot, base, last, onOpen }: {
  pot: Pot; base: string; last: boolean; onOpen: () => void;
}) {
  const grew = pot.delta !== null && !pot.delta.startsWith("-");
  return (
    <button type="button" onClick={() => { tap(); onOpen(); }}
      style={rowButton(last)}>
      {/* Дельта переехала под СУММУ, а не под имя. Слева читалось «наличные
          −50», будто это и есть остаток; на деле −50 относится к 900 справа, и
          два числа обязаны стоять в одной колонке. */}
      <div style={{ flexGrow: 1, minWidth: 0 }}>
        <div style={{ fontSize: "var(--f-body)", fontWeight: 500, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pot.name}</div>
        {pot.currency !== base && (
          <div className="num" style={{ fontSize: "var(--f-caption-s)", marginTop: 2,
                                        color: "var(--text-muted)" }}>
            {formatMoney(pot.amount, pot.currency)}
          </div>
        )}
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div className="num" style={{ fontSize: "var(--f-body)", fontWeight: 500 }}>
          {formatMoney(pot.amount_base, base)}
        </div>
        <div className="num" style={{ fontSize: "var(--f-caption)", marginTop: 2,
                                      color: grew ? "var(--income)" : "var(--text-muted)" }}>
          {/* Сервер прислал дельту строкой; formatMoney сам ставит минус */}
          {pot.delta === null ? "новая" : `${grew ? "+" : ""}${formatMoney(pot.delta, base)}`}
        </div>
      </div>
    </button>
  );
}

/** Обновить кошельки (MA-FR-15).
 *
 * Если прошлый снимок есть — поля по его кошелькам, предзаполненные прошлыми
 * числами: пересъёмка это правка, а не ввод с нуля. Раз в месяц перечислять
 * фразой десять кошельков с их валютами — работа, которую человек делал за
 * приложение, хотя приложение уже знает и названия, и валюты.
 *
 * Первый снимок — фразой: кучек ещё нет, править нечего, а форма с одним пустым
 * полем хуже фразы, в которой можно перечислить всё сразу.
 */
export function SnapshotSheet({ previous, onClose, onDone }: {
  previous: Pot[]; onClose: () => void; onDone: () => void;
}) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState(() =>
    previous.map((p) => ({ name: p.name, amount: p.amount, currency: p.currency })));
  const [extra, setExtra] = useState<{ name: string; amount: string; currency: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");
  const byForm = previous.length > 0;

  const once = useOnce();

  const submitForm = () => {
    tap();
    setBusy(true);
    setTrouble("");
    const all = [...rows, ...extra].filter((r) => r.name.trim() && r.amount.trim());
    once(() => api.snapshotPots(all.map((r) => ({ ...r, amount: r.amount.trim() })))
      .then((out) => (out.kind === "capital_taken"
        ? onDone()
        : setTrouble(out.hint || "Не получилось сохранить.")))
      .catch(() => setTrouble("Не получилось сохранить. Попробуй ещё раз."))
      .finally(() => setBusy(false)));
  };

  const submit = () => {
    tap();
    setBusy(true);
    setTrouble("");
    once(() => api.snapshot(text.trim())
      .then((out) => (out.kind === "capital_taken"
        ? onDone()
        : setTrouble(out.hint || "Не разобрал снимок. Например: «тинькофф 300к, наличка 5 млн донгов».")))
      .catch(() => setTrouble("Не получилось сохранить. Попробуй ещё раз."))
      .finally(() => setBusy(false)));
  };

  return (
    <Sheet title="Кошельки на сегодня" onClose={onClose}>
      <p style={{ ...hint, marginTop: 0, marginBottom: "var(--s-4)" }}>
        {byForm
          ? "Поправь, что изменилось. Ноль означает «кошелька больше нет» — она просто не попадёт в снимок."
          : "Сколько сейчас лежит по кошелькам. Пересъёмка того же дня заменяет его целиком."}
      </p>

      {byForm ? (
        <>
          {[...rows, ...extra].map((row, i) => {
            const own = i < rows.length;
            const update = (patch: Partial<typeof row>) => {
              const next = { ...row, ...patch };
              own ? setRows(rows.map((r, j) => (j === i ? next : r)))
                  : setExtra(extra.map((r, j) => (j === i - rows.length ? next : r)));
            };
            return (
              <div key={own ? `p${i}` : `e${i}`}
                   style={{ display: "flex", gap: "var(--s-2)", marginBottom: "var(--s-2)" }}>
                <input value={row.name} onChange={(e) => update({ name: e.target.value })}
                       maxLength={64} aria-label={`Кошелёк ${i + 1}`} readOnly={own}
                       style={{ ...field, flexGrow: 1, minWidth: 0,
                                color: own ? "var(--text-muted)" : "var(--text)" }} />
                <input value={row.amount} onChange={(e) => update({ amount: e.target.value })}
                       inputMode="decimal" aria-label={`Сумма: ${row.name || i + 1}`}
                       style={{ ...field, width: "38%", minWidth: 0 }} />
                <select value={row.currency} onChange={(e) => update({ currency: e.target.value })}
                        aria-label={`Валюта: ${row.name || i + 1}`}
                        style={{ ...field, width: "auto", padding: "0 8px" }}>
                  <option value="USD">$</option>
                  <option value="VND">₫</option>
                  <option value="RUB">₽</option>
                </select>
              </div>
            );
          })}
          <button type="button"
                  onClick={() => { tap(); setExtra([...extra,
                    { name: "", amount: "", currency: "USD" }]); }}
                  style={{ background: "none", border: "none", cursor: "pointer", font: "inherit",
                           fontSize: "var(--f-caption)", color: "var(--accent)",
                           padding: "10px 0", minHeight: 38 }}>
            + Новый кошелёк
          </button>
          {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
          <button type="button" disabled={busy} onClick={submitForm}
                  style={{ ...primary, marginTop: "var(--s-3)" }}>
            Сохранить снимок
          </button>
        </>
      ) : (
      <>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
        placeholder="Тинькофф 300к, наличка 5 млн донгов, Bybit 3188 usd"
        aria-label="Кошельки на сегодня"
        style={{ width: "100%", background: "var(--surface-2)", border: "none",
                 borderRadius: "var(--r-field)", padding: "12px 14px", color: "var(--text)",
                 font: "inherit", fontSize: "var(--f-body)", resize: "none" }} />
      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
      <button type="button" disabled={!text.trim() || busy} onClick={submit}
              style={{ ...primary, marginTop: "var(--s-4)",
                       background: text.trim() && !busy ? "var(--accent)" : "var(--surface-2)",
                       color: text.trim() && !busy ? "var(--on-accent)" : "var(--text-disabled)" }}>
        Сохранить
      </button>
      </>
      )}
    </Sheet>
  );
}

const field: React.CSSProperties = {
  minHeight: "var(--tap)", background: "var(--surface-2)", border: "none",
  borderRadius: "var(--r-field)", padding: "12px 14px", color: "var(--text)",
  font: "inherit", fontSize: "var(--f-body)",
};

function PotSheet({ pot, onClose, onDone }: { pot: Pot; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(pot.name);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  const run = (call: () => Promise<unknown>) => {
    if (busy) return;
    tap();
    setBusy(true);
    setTrouble("");
    // Молча гасить спиннер нельзя: человек решит, что сохранилось.
    call().then(onDone)
      .catch(() => setTrouble("Не получилось сохранить. Попробуй ещё раз."))
      .finally(() => setBusy(false));
  };

  return (
    <Sheet title={pot.name} onClose={onClose}>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
             aria-label="Название кошелька"
             style={{ width: "100%", minHeight: "var(--tap)", background: "var(--surface-2)",
                      border: "none", borderRadius: "var(--r-field)", padding: "12px 14px",
                      color: "var(--text)", font: "inherit", fontSize: "var(--f-body)",
                       }} />
      <p style={hint}>
        Переименование меняет кошелёк во всей истории — иначе кривая разорвётся на «Тинькофф»
        до и «Т-Банк» после, и это выглядело бы как исчезнувшие деньги.
      </p>
      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
      <button type="button" disabled={busy || !name.trim() || name.trim() === pot.name}
              onClick={() => run(() => api.renamePot(pot.name, name.trim()))}
              style={{ ...primary, marginTop: "var(--s-4)" }}>
        Сохранить
      </button>
      <button type="button" aria-disabled={busy || undefined} onClick={() => run(() => api.dropPot(pot.name))}
              style={{ ...primary, marginTop: "var(--s-2)", background: "transparent",
                       color: "var(--danger)",
                       border: "1px solid color-mix(in srgb, var(--danger) 30%, transparent)" }}>
        Убрать из снимка
      </button>
    </Sheet>
  );
}

function Empty({ onAdd }: { onAdd: () => void }) {
  return (
    <div style={{ ...card, textAlign: "center", padding: "var(--s-8) var(--s-5)" }}>
      <p style={{ fontSize: "var(--f-body)", fontWeight: 600, margin: 0 }}>
        Кошельки ещё не заполнены
      </p>
      <p style={{ ...hint, marginTop: "var(--s-2)" }}>
        Раз в месяц вносишь, сколько где лежит, — и видно, куда идёт капитал.
      </p>
      <button type="button" onClick={onAdd} style={{ ...primary, marginTop: "var(--s-4)" }}>
        Сделать снимок
      </button>
    </div>
  );
}

function Skeleton() {
  return (
    <div style={card} role="status" aria-busy="true" aria-label="Загружаю капитал">
      <div style={{ height: 30, width: "45%", background: "var(--surface-3)", borderRadius: 7 }} />
      <div style={{ height: 96, background: "var(--surface-2)", borderRadius: 10,
                    marginTop: "var(--s-4)" }} />
    </div>
  );
}

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня",
                "июля", "августа", "сентября", "октября", "ноября", "декабря"];

function formatDay(iso: string | null): string {
  if (!iso) return "";
  const [, month, date] = iso.split("-");
  return `${Number(date)} ${MONTHS[Number(month) - 1]}`;
}



const primary: React.CSSProperties = {
  width: "100%", minHeight: "var(--tap)", padding: "14px", borderRadius: "var(--r-field)",
  border: "none", cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
  fontWeight: 600, background: "var(--accent)", color: "var(--on-accent)",
};
